/**
 * The Properties panel's unsaved-changes guard (UI/UX fix round A, review A2): Close and Reload with dirty sections ask
 * Save / Discard / Cancel instead of dropping the drafts, and a reload discards a section only after the re-read
 * succeeded — a failed reload keeps the edits and says so. While a reload is in flight `reloading` is true and the panel
 * locks its editors (review UXA-02: a successful reload must never drop what was typed while it was pending).
 *
 * "Save" saves every dirty section in turn (metadata, headers, index tags). A metadata or header write changes the blob's
 * ETag, so each write after the first is conditioned on the version the previous write's re-read returned — but only
 * after that re-read shows the section's content still equal to the baseline its draft started from (review UXA-01:
 * another client may have changed it between the previous write and the re-read). A section that changed there, or
 * whose re-read is missing, is not written: Save stops, keeps the dialog open and says why. A section that cannot be
 * saved at all (invalid rows, changed on the server, locked, version unknown) makes Save unavailable with its reason.
 *
 * Final UI/UX batch C1 (review R1): each Save-all owns an operation token. Cancel (or Discard, or a new guard action)
 * invalidates it, and after every await the loop checks the token AND the drafts' edit revision: a cancelled or
 * out-of-date Save records what already landed but writes no further section and never runs the Close / Reload it was
 * asked for — an edit made after Cancel is never discarded by the panel closing underneath it. Nor by the write landing
 * (review C1-01): the draft reducer records each section write at dispatch, so an edit made after it — even one back to
 * the original value — stays a draft against the landed value (`propertiesDraftState` `dispatch` / `landedEdit`).
 */
import { useCallback, useRef, useState } from 'react';
import type { StorageBlobHttpHeaders, StorageResourceProperties } from '../../../../../types/azureStorage';
import type { DraftSection, HeaderSectionView, MapSectionView } from '../propertiesDraftState';
import type { PropertiesGuardAction } from '../PropertiesUnsavedDialog';
import type { StorageSaveResult } from './useStoragePropertiesSave';

const SECTION_NAMES: Record<DraftSection, string> = { metadata: 'Metadata', headers: 'HTTP headers', tags: 'Index tags' };

/** `unchangedIn(reread)`: the section's content in a re-read still equals the baseline its draft started from. */
type GuardSectionBase = { lockedReason?: string; unchangedIn: (reread: StorageResourceProperties) => boolean };
export type GuardSection =
  | GuardSectionBase & { section: 'metadata' | 'tags'; view: MapSectionView; save: (value: Record<string, string>, etag: string | undefined) => Promise<StorageSaveResult> }
  | GuardSectionBase & { section: 'headers'; view: HeaderSectionView; save: (value: StorageBlobHttpHeaders, etag: string | undefined) => Promise<StorageSaveResult> };

type Args = {
  sections: GuardSection[];
  onSaved: (section: DraftSection, value: Record<string, string> | StorageBlobHttpHeaders, result: StorageSaveResult) => void;
  discard: (section?: DraftSection) => void;
  refetchNow: () => Promise<boolean>;
  close: () => void;
  /** The drafts' edit revision (bumped by every user edit): a reload discards only the revision it began with. */
  revision: number;
};

const etagOf = (value: StorageResourceProperties | undefined): string | undefined => (typeof value?.properties.etag === 'string' ? value.properties.etag : undefined);

const changedReason = (section: DraftSection): string => `${SECTION_NAMES[section]} changed on the server; reload it before saving.`;
/** R1: an edit landed while Save-all was writing (it cannot while the dialog is open, but a programmatic one could). */
export const EDITED_DURING_SAVE_REASON = 'The properties were edited while saving; nothing else was saved. Check them and save again.';
const uncheckedReason = (section: DraftSection): string => `${SECTION_NAMES[section]} could not be checked against the server after the previous save. Reload it before saving.`;

/** Why the dirty sections cannot all be saved from the guard, or undefined when they can. */
export function guardSaveReason(sections: GuardSection[]): string | undefined {
  for (const { section, view, lockedReason } of sections.filter((candidate) => candidate.view.dirty)) {
    if (lockedReason) return lockedReason;
    if (view.conflict) return changedReason(section);
    if ('ok' in view.draft && !view.draft.ok) return `${SECTION_NAMES[section]}: ${view.draft.message}`;
    if (!view.etag) return `${SECTION_NAMES[section]}: reload before saving — the version this edit is based on is unknown.`;
  }
  return undefined;
}

export function usePropertiesPanelGuard({ sections, onSaved, discard, refetchNow, close, revision }: Args) {
  const [action, setAction] = useState<PropertiesGuardAction | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadFailed, setReloadFailed] = useState(false);
  const [reloading, setReloading] = useState(false);
  /** Why Save-all stopped part-way (UXA-01); cleared by the next guard action. */
  const [stopped, setStopped] = useState<string>();
  const dirty = sections.filter((candidate) => candidate.view.dirty);

  const revisionRef = useRef(revision);
  revisionRef.current = revision;
  const latestReload = useRef(0);
  /** R1: the Save-all that may still act; Cancel / Discard / a new request move it on. */
  const latestSave = useRef(0);

  /**
   * Reload that keeps the drafts unless the re-read succeeds; the editors are locked meanwhile (UXA-02). Each reload
   * owns a token (r2): a superseded one — React Query cancels it when the next one starts — can neither unlock the
   * editors nor discard; only the latest one does, and it discards only if no edit landed since it began.
   */
  const reloadNow = useCallback(async (section?: DraftSection) => {
    const token = ++latestReload.current;
    const startRevision = revisionRef.current;
    setReloading(true);
    try {
      const ok = await refetchNow();
      if (token !== latestReload.current) return;
      setReloadFailed(!ok);
      if (ok && revisionRef.current === startRevision) discard(section);
    } finally {
      if (token === latestReload.current) setReloading(false);
    }
  }, [refetchNow, discard]);

  const run = useCallback((next: PropertiesGuardAction) => {
    if (next === 'close') close();
    else void reloadNow();
  }, [close, reloadNow]);

  const request = useCallback((next: PropertiesGuardAction) => {
    latestSave.current += 1;
    setStopped(undefined);
    if (dirty.length > 0) setAction(next);
    else run(next);
  }, [dirty.length, run]);

  const saveAll = useCallback(async () => {
    if (!action || stopped || guardSaveReason(sections)) return;
    const token = ++latestSave.current;
    const startRevision = revisionRef.current;
    /** R1: still the Save the user asked for — not cancelled, superseded, or outrun by a newer edit. */
    const current = () => token === latestSave.current && revisionRef.current === startRevision;
    setSaving(true);
    let previous: { reread?: StorageResourceProperties } | undefined;
    try {
      for (const entry of dirty) {
        // UXA-01: after a write, the next section is written only if the re-read shows it as its draft's baseline.
        if (previous) {
          const why = !previous.reread ? uncheckedReason(entry.section) : !entry.unchangedIn(previous.reread) ? changedReason(entry.section) : undefined;
          if (why) { setStopped(why); return; } // the dialog stays open with the reason; saved sections stay saved
        }
        const etag = previous ? etagOf(previous.reread) : entry.view.etag;
        const result = entry.section === 'headers'
          ? await entry.save(entry.view.draft, etag)
          : entry.view.draft.ok ? await entry.save(entry.view.draft.value, etag) : { ok: false as const };
        // A write that landed is recorded even after a Cancel (it cannot be recalled); nothing after it runs then.
        if (result.ok) onSaved(entry.section, entry.section === 'headers' ? entry.view.draft : (entry.view.draft.ok ? entry.view.draft.value : {}), result);
        if (!current()) {
          if (token === latestSave.current) setStopped(EDITED_DURING_SAVE_REASON);
          return;
        }
        if (!result.ok) { setAction(null); return; } // the section shows why; the drafts stay
        previous = { reread: result.reread };
      }
      const next = action;
      setAction(null);
      run(next);
    } finally {
      if (token === latestSave.current) setSaving(false);
    }
  }, [action, stopped, sections, dirty, onSaved, run]);

  return {
    action, saving, reloading, reloadFailed, request, reloadNow, saveAll,
    discardAndRun: () => { latestSave.current += 1; setSaving(false); const next = action; setAction(null); setStopped(undefined); if (!next) return; if (next === 'close') { discard(); close(); } else void reloadNow(); },
    cancel: () => { latestSave.current += 1; setSaving(false); setAction(null); setStopped(undefined); },
    dirtyNames: dirty.map((entry) => SECTION_NAMES[entry.section]),
    saveReason: stopped ?? guardSaveReason(sections),
  };
}
